import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export type UseAsyncAutocompleteOptions<T> = {
  /** Gọi API / loader theo từ khóa tìm kiếm (đã debounce). */
  fetchOptions: (search: string) => Promise<T[]>;
  /** Bật/tắt fetch (vd. khi tab ẩn hoặc radio chưa chọn). */
  enabled?: boolean;
  /** Giá trị đã chọn. */
  value: T | null;
  /** Nhãn hiển thị trong ô input khi đã chọn. */
  getOptionLabel: (option: T) => string;
  /** So khớp option (mặc định dùng getOptionLabel). */
  getOptionId?: (option: T) => string | number;
  debounceMs?: number;
  /** Gọi lại khi mở dropdown (mặc định true). */
  refreshOnOpen?: boolean;
};

export type UseAsyncAutocompleteResult<T> = {
  options: T[];
  loading: boolean;
  inputValue: string;
  setInputValue: (value: string) => void;
  debouncedSearch: string;
  refresh: (search?: string) => Promise<void>;
  reset: () => void;
  /** Props dùng trực tiếp cho MUI Autocomplete. */
  autocompleteProps: {
    options: T[];
    loading: boolean;
    inputValue: string;
    filterOptions: <O extends T>(options: O[]) => O[];
    onInputChange: (
      event: React.SyntheticEvent,
      newInputValue: string,
      reason: string
    ) => void;
    onOpen: () => void;
  };
};

const defaultGetOptionId = <T,>(option: T): string | number => {
  if (option && typeof option === 'object' && 'id' in option) {
    const id = (option as { id?: string | number }).id;
    if (id !== undefined && id !== null && id !== '') {
      return id;
    }
  }
  return String(option);
};

export function useAsyncAutocomplete<T>({
  fetchOptions,
  enabled = true,
  value,
  getOptionLabel,
  getOptionId = defaultGetOptionId,
  debounceMs = 350,
  refreshOnOpen = true,
}: UseAsyncAutocompleteOptions<T>): UseAsyncAutocompleteResult<T> {
  const [options, setOptions] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const requestRef = useRef(0);
  const selectedIdRef = useRef<string | number | null>(null);
  const fetchOptionsRef = useRef(fetchOptions);

  fetchOptionsRef.current = fetchOptions;

  const refresh = useCallback(async (search?: string) => {
    const requestId = ++requestRef.current;
    setLoading(true);

    try {
      const nextOptions = await fetchOptionsRef.current(search?.trim() ?? '');
      if (requestId !== requestRef.current) {
        return;
      }
      setOptions(nextOptions);
    } catch (error) {
      if (requestId === requestRef.current) {
        console.error('[useAsyncAutocomplete] fetchOptions failed:', error);
        setOptions([]);
      }
    } finally {
      if (requestId === requestRef.current) {
        setLoading(false);
      }
    }
  }, []);

  const reset = useCallback(() => {
    requestRef.current += 1;
    setOptions([]);
    setLoading(false);
    setInputValue('');
    setDebouncedSearch('');
    selectedIdRef.current = null;
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(inputValue.trim());
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [inputValue, debounceMs]);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    void refresh(debouncedSearch);
  }, [enabled, debouncedSearch, refresh]);

  useEffect(() => {
    const nextId = value ? getOptionId(value) : null;
    if (nextId === selectedIdRef.current) {
      return;
    }
    selectedIdRef.current = nextId;
    setInputValue(value ? getOptionLabel(value) : '');
  }, [value, getOptionId, getOptionLabel]);

  const mergedOptions = useMemo(() => {
    if (!value) {
      return options;
    }
    const selectedId = getOptionId(value);
    const exists = options.some((option) => getOptionId(option) === selectedId);
    if (exists) {
      return options;
    }
    return [value, ...options];
  }, [options, value, getOptionId]);

  const onInputChange = useCallback(
    (_event: React.SyntheticEvent, newInputValue: string, reason: string) => {
      if (reason === 'reset') {
        return;
      }
      setInputValue(newInputValue);
    },
    []
  );

  const onOpen = useCallback(() => {
    if (refreshOnOpen) {
      void refresh(debouncedSearch);
    }
  }, [refreshOnOpen, refresh, debouncedSearch]);

  const autocompleteProps = useMemo(
    () => ({
      options: mergedOptions,
      loading,
      inputValue,
      filterOptions: <O extends T>(opts: O[]) => opts,
      onInputChange,
      onOpen,
    }),
    [mergedOptions, loading, inputValue, onInputChange, onOpen]
  );

  return {
    options: mergedOptions,
    loading,
    inputValue,
    setInputValue,
    debouncedSearch,
    refresh,
    reset,
    autocompleteProps,
  };
}
